import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, UserPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

const STATO = { invitato: "bg-amber-500/15 text-amber-500", attivo: "bg-green-600/15 text-green-500", disattivato: "bg-neutral-500/15 text-neutral-400" };

export default function VestaAccessCard({ partnerId }) {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/partners/${partnerId}/vesta-users`).then((r) => setData(r.data)).catch(() => setData({ users: [] })), [partnerId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };
  const invite = () => run(async () => { await api.post(`/partners/${partnerId}/vesta-invite`, { name, email }); setName(""); setEmail(""); }, "Invito inviato");

  if (!data) return null;
  return (
    <Card className="p-5" data-testid="vesta-access-card">
      <div className="mb-3 flex items-center gap-2">
        <img src="/vesta-badge.png" alt="" className="h-5 w-5" />
        <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-[#C17F59]">Accessi <span className="normal-case">Ai VESTA</span></h3>
      </div>
      {data.users.length === 0 && <p className="mb-3 text-xs text-muted-foreground">Nessuna persona abilitata.</p>}
      <div className="space-y-2">
        {data.users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-sm" data-testid={`vesta-user-${u.id}`}>
            <span className="font-semibold">{u.name}</span>
            <span className="text-xs text-muted-foreground">{u.email}</span>
            <Badge className={`${STATO[u.stato]} border-0 text-[10px]`} data-testid={`vesta-user-stato-${u.id}`}>{u.stato}</Badge>
            {data.can_invite && (
              <span className="ml-auto flex items-center gap-2">
                {u.stato === "invitato" && <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => api.post(`/vesta-users/${u.id}/reinvita`), "Invito reinviato")} data-testid={`vesta-reinvite-${u.id}`}><Send className="mr-1 h-3.5 w-3.5" /> Reinvia</Button>}
                <Switch checked={u.stato !== "disattivato"} disabled={busy} onCheckedChange={(v) => run(() => api.post(`/vesta-users/${u.id}/stato`, { attivo: v }), v ? "Accesso riattivato" : "Accesso disattivato")} data-testid={`vesta-toggle-${u.id}`} />
              </span>
            )}
          </div>
        ))}
      </div>
      {data.can_invite && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome e cognome" className="h-9 max-w-[200px]" data-testid="vesta-invite-name" />
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" type="email" className="h-9 max-w-[240px]" data-testid="vesta-invite-email" />
          <Button size="sm" disabled={busy || !name.trim() || !email.trim()} onClick={invite} className="bg-[#C17F59] text-black hover:bg-[#C17F59]/90" data-testid="vesta-invite-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="mr-1 h-4 w-4" /> Invita ad Ai VESTA</>}
          </Button>
        </div>
      )}
    </Card>
  );
}
